import React, { useState } from 'react';
import {
  FileText, Download, X, CheckCircle, Shield, BarChart3, FileCheck,
  Layers, RefreshCw, Check, Clock, Building2, Calendar, User,
  Lock, Eye, Award, AlertTriangle
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * AuditPackExportModal Component
 * Generates comprehensive branded PDF audit packs
 */
const AuditPackExportModal = ({ isOpen, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [success, setSuccess] = useState(false);
  
  const [settings, setSettings] = useState({
    packName: 'ESG Disclosure Audit Pack',
    companyName: 'Acme Corporation',
    reportingPeriod: '2024',
    includeSections: ['cover', 'executive_summary', 'disclosures', 'evidence', 'appendix'],
    includeSignatures: true,
    includeAuditTrail: true,
    format: 'pdf'
  });

  const sections = [
    { id: 'cover', label: 'Cover Page', icon: FileText, desc: 'Branded title page with company info' },
    { id: 'executive_summary', label: 'Executive Summary', icon: BarChart3, desc: 'Readiness metrics and highlights' },
    { id: 'disclosures', label: 'Disclosure Details', icon: Layers, desc: 'All ESRS standards by category' },
    { id: 'evidence', label: 'Evidence Documentation', icon: FileCheck, desc: 'Linked evidence items' },
    { id: 'appendix', label: 'Appendix', icon: Shield, desc: 'Methodology, sources, limitations' }
  ];

  const toggleSection = (sectionId) => {
    setSettings(prev => ({
      ...prev,
      includeSections: prev.includeSections.includes(sectionId)
        ? prev.includeSections.filter(s => s !== sectionId)
        : [...prev.includeSections, sectionId]
    }));
  };

  const generateAuditPack = async () => {
    setLoading(true);
    setProgress(0);
    setSuccess(false);
    
    // Simulate progress
    const progressInterval = setInterval(() => {
      setProgress(prev => Math.min(prev + 10, 90));
    }, 300);
    
    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token') || '';
      const response = await fetch(`${API}/api/audit-pack/export-pdf`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          pack_name: settings.packName,
          company_name: settings.companyName,
          reporting_period: settings.reportingPeriod,
          include_sections: settings.includeSections,
          include_signatures: settings.includeSignatures,
          include_audit_trail: settings.includeAuditTrail,
          format: settings.format
        })
      });

      if (!response.ok) {
        throw new Error(`Export failed with status ${response.status}`);
      }
      const blob = await response.blob();

      clearInterval(progressInterval);
      setProgress(100);

      // Download the file
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${settings.companyName.replace(/\s+/g, '_')}_Audit_Pack_${settings.reportingPeriod}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      setSuccess(true);
      setTimeout(() => {
        setSuccess(false);
        setProgress(0);
      }, 3000);
      
    } catch (err) {
      console.error('Error generating audit pack:', err);
      alert('Failed to generate audit pack. Please try again.');
      clearInterval(progressInterval);
      setProgress(0);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="audit-pack-export-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 bg-gradient-to-r from-indigo-600 to-purple-600 text-white flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Award size={28} />
            <div>
              <h2 className="text-xl font-bold">Export Audit Pack</h2>
              <p className="text-indigo-200 text-sm">Generate comprehensive PDF for external auditors</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-white/20 rounded-lg transition-colors">
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Basic Info */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Company Name</label>
              <div className="flex items-center gap-2">
                <Building2 size={18} className="text-slate-400" />
                <input
                  type="text"
                  value={settings.companyName}
                  onChange={(e) => setSettings({ ...settings, companyName: e.target.value })}
                  className="flex-1 px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Reporting Period</label>
              <div className="flex items-center gap-2">
                <Calendar size={18} className="text-slate-400" />
                <select
                  value={settings.reportingPeriod}
                  onChange={(e) => setSettings({ ...settings, reportingPeriod: e.target.value })}
                  className="flex-1 px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="2024">FY 2024</option>
                  <option value="2025">FY 2025</option>
                  <option value="2026">FY 2026</option>
                </select>
              </div>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Audit Pack Title</label>
            <input
              type="text"
              value={settings.packName}
              onChange={(e) => setSettings({ ...settings, packName: e.target.value })}
              className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
            />
          </div>

          {/* Section Selection */}
          <div>
            <h3 className="text-sm font-semibold text-slate-700 mb-3">Include Sections</h3>
            <div className="grid grid-cols-1 gap-2">
              {sections.map((section) => {
                const Icon = section.icon;
                const isSelected = settings.includeSections.includes(section.id);
                return (
                  <button
                    key={section.id}
                    onClick={() => toggleSection(section.id)}
                    className={`flex items-center gap-4 p-4 rounded-xl border-2 transition-all text-left ${
                      isSelected
                        ? 'border-indigo-500 bg-indigo-50'
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                    data-testid={`section-${section.id}`}
                  >
                    <div className={`p-2 rounded-lg ${isSelected ? 'bg-indigo-100 text-indigo-600' : 'bg-slate-100 text-slate-500'}`}>
                      <Icon size={20} />
                    </div>
                    <div className="flex-1">
                      <p className={`font-medium ${isSelected ? 'text-indigo-700' : 'text-slate-700'}`}>{section.label}</p>
                      <p className="text-xs text-slate-500">{section.desc}</p>
                    </div>
                    <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                      isSelected ? 'border-indigo-500 bg-indigo-500' : 'border-slate-300'
                    }`}>
                      {isSelected && <Check size={14} className="text-white" />}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Additional Options */}
          <div className="grid grid-cols-2 gap-4">
            <div className="p-4 bg-slate-50 rounded-xl border border-slate-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <User size={18} className="text-slate-500" />
                  <span className="text-sm font-medium text-slate-700">Include Signature Fields</span>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={settings.includeSignatures}
                    onChange={(e) => setSettings({ ...settings, includeSignatures: e.target.checked })}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600"></div>
                </label>
              </div>
              <p className="text-xs text-slate-500 mt-2">Add fields for Sustainability Lead, CFO, and External Auditor signatures</p>
            </div>

            <div className="p-4 bg-slate-50 rounded-xl border border-slate-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Lock size={18} className="text-slate-500" />
                  <span className="text-sm font-medium text-slate-700">Include Audit Trail</span>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={settings.includeAuditTrail}
                    onChange={(e) => setSettings({ ...settings, includeAuditTrail: e.target.checked })}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600"></div>
                </label>
              </div>
              <p className="text-xs text-slate-500 mt-2">Include document hash, generation timestamp, and platform version</p>
            </div>
          </div>

          {/* Preview Summary */}
          <div className="p-4 bg-indigo-50 border border-indigo-200 rounded-xl">
            <h4 className="font-semibold text-indigo-700 mb-2 flex items-center gap-2">
              <Eye size={18} />
              Export Preview
            </h4>
            <div className="grid grid-cols-3 gap-4 text-sm">
              <div>
                <p className="text-indigo-600 font-medium">Sections</p>
                <p className="text-indigo-800">{settings.includeSections.length} of {sections.length}</p>
              </div>
              <div>
                <p className="text-indigo-600 font-medium">Format</p>
                <p className="text-indigo-800">PDF Document</p>
              </div>
              <div>
                <p className="text-indigo-600 font-medium">Est. Pages</p>
                <p className="text-indigo-800">{settings.includeSections.length * 3 + 2} pages</p>
              </div>
            </div>
          </div>

          {/* Progress Bar */}
          {loading && (
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-slate-600">Generating audit pack...</span>
                <span className="text-indigo-600 font-medium">{progress}%</span>
              </div>
              <div className="w-full h-3 bg-slate-200 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-gradient-to-r from-indigo-500 to-purple-500 transition-all duration-300"
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>
          )}

          {/* Success Message */}
          {success && (
            <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-xl text-center">
              <CheckCircle size={32} className="mx-auto text-emerald-500 mb-2" />
              <p className="text-emerald-700 font-medium">Audit Pack Generated Successfully!</p>
              <p className="text-emerald-600 text-sm">Your download should start automatically.</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-slate-200 bg-slate-50 flex items-center justify-between">
          <div className="flex items-center gap-2 text-xs text-slate-500">
            <Shield size={14} />
            <span>CSRD & ESRS Compliant Format</span>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={onClose} className="px-4 py-2 text-slate-600 hover:text-slate-900 font-medium">
              Cancel
            </button>
            <button
              onClick={generateAuditPack}
              disabled={loading || settings.includeSections.length === 0}
              className="px-6 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg font-medium flex items-center gap-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="generate-audit-pack-btn"
            >
              {loading ? (
                <>
                  <RefreshCw size={18} className="animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <Download size={18} />
                  Generate PDF
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuditPackExportModal;
